import {
  Component,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { UntypedFormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect, MatOption } from '@angular/material/select';
import { ClientByContact } from '@models/dashboard/model';
import { Contact } from '@models/profile/model';
import { Observable, Subscription } from 'rxjs';
import { first, tap } from 'rxjs';
import { Categories } from 'src/app/shared/constants/analyticsCategory.constant';
import { BrandService } from 'src/app/shared/services/brand.service';
import { ErrorNotificationService } from 'src/app/shared/services/error-notification.service';
import { GoogleAnalyticsService } from 'src/app/shared/services/google-analytics.service';
import { SharedService } from 'src/app/shared/services/shared.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { ContactQuery, CurrentUserQuery } from 'src/app/state';

import { AuthenticationService } from '../auth/services/authentication.service';
import { CommonConstants } from './../../shared/constants/common.constant';
import { DashboardService } from '@app/shared/services/dashboard.service';
import { MatFormField, MatLabel } from '@angular/material/input';
import { MatSelectSearchComponent } from 'ngx-mat-select-search';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-distributor',
  templateUrl: './distributor.component.html',
  styleUrls: ['./distributor.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatFormField, MatLabel, MatSelect, FormsModule, ReactiveFormsModule, MatOption, MatSelectSearchComponent, MatButton, MatProgressSpinner, AsyncPipe],
})
export class DistributorComponent implements OnInit, OnDestroy {
  private authenticationService = inject(AuthenticationService);
  private dashboardService = inject(DashboardService);
  private contactQuery = inject(ContactQuery);
  public renderer = inject(Renderer2);
  public dialog = inject(MatDialog);
  private sharedService = inject(SharedService);
  private errorNotificationService = inject(ErrorNotificationService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private brandService = inject(BrandService);
  private utilityService = inject(UtilityService);
  private currentUserQuery = inject(CurrentUserQuery);

  public loading: boolean;
  public contactName: string;
  public numberOfClients: number;
  public contacts$: Observable<Contact[]>;
  public allClients: ClientByContact[];
  public allClients$: Observable<ClientByContact[]>;

  /** control for the selected client */
  public selectedClientCtrl: UntypedFormControl = new UntypedFormControl('', [Validators.required]);

  /** control for the MatSelect filter keyword */
  public clientFilterCtrl: UntypedFormControl = new UntypedFormControl();

  /** list of clients filtered by search keyword */
  public filteredClients: ClientByContact[] = [];
  public readonly brand = this.brandService.getBrandResources();

  @ViewChild('singleSelect') public singleSelect: MatSelect;
  private subscriptions = new Subscription();

  public ngOnInit(): void {
    this.subscriptions.add(
      this.errorNotificationService.errorNotification.subscribe(() => {
        this.loading = false;
        this.utilityService.showGrowelMessage(this.sharedService.getMessageByBrand(CommonConstants.errorCodes.apiFailure), false);
      })
    );

    this.contacts$ = this.contactQuery.selectAllWhenLoaded().pipe(
      tap((contacts) => {
        if (contacts?.length) {
          const currentContact = contacts.find((contact) => contact.email.toLowerCase() === this.currentUserQuery.getEmailAddress());
          this.contactName = `${currentContact?.firstName} ${currentContact?.lastName}`;
        }
      })
    );

    this.allClients$ = this.dashboardService.loadClientsByContact().pipe(
      first(),
      tap((clients) => {
        this.numberOfClients = clients.length;
        this.allClients = clients;
        /** load the initial clients list */
        this.filteredClients = this.allClients;
      })
    );

    /** listen for search field value changes */
    this.subscriptions.add(
      this.clientFilterCtrl.valueChanges.subscribe(() => {
        this.filterClients();
      })
    );
  }

  /**
   * This method is triggered when user selects the client and clicks on continue.
   * This calls the token service to get the new token based on the selected client.
   * @returns {void}
   * @memberof DistributorComponent
   */
  public async onClientSelect(): Promise<void> {
    await this.authenticateAndRedirectUser();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private async authenticateAndRedirectUser(): Promise<void> {
    this.loading = true;
    this.googleAnalyticsService.eventEmitter(Categories.EventCategories.SelectClient);
    const clientId = this.selectedClientCtrl.value.clientId as string;
    await this.authenticationService.signInWithStoredCredentials({ clientId }, true, { keepCredentials: true });
    this.loading = false;
  }

  private filterClients(): void {
    // get the search keyword
    let search = this.clientFilterCtrl.value;
    if (!search) {
      this.filteredClients = this.allClients;
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter clients
    this.filteredClients = this.allClients.filter((cbc) => cbc.clientName.toLowerCase().indexOf(search) > -1);
  }
}
